import { getGroupDisplayLabel } from '../../../lib/sessionGroupingUtils';
import { SessionAssetList } from './SessionAssetList';
import { SessionDesktopGroupTimer, SessionTimerResetDialog } from './SessionTimers';
import MobileAssetGroupDefault from './cards/MobileAssetGroupDefault';
import MobileAssetGroupEmerg from './cards/MobileAssetGroupEmerg';
import { deriveMobileGroupCardFields } from './cards/mobileGroupCardData';

/**
 * Controlled grouped-assets workspace: desktop two-pane (selectable list +
 * resizer) and mobile collapsible groups.
 *
 * Presentational only. The parent orchestrator owns all grouping state, the
 * timer store reads/mutations, and the asset-list callbacks. The verbatim
 * SessionAssetList props are passed through as a single `assetListProps` bag.
 */
export function AssetGroupList({
  groupedAssets,
  sessionAssetType,
  isExitEmergency,
  workspaceRef,
  leftPaneWidthPx,
  isResizing,
  setIsResizing,
  selectedGroupKey,
  setSelectedGroupKey,
  expandedGroupKeys,
  setExpandedGroupKeys,
  timerResetGroupName,
  setTimerResetGroupName,
  getTimerStatus,
  getTimerRemainingMs,
  getTimerStartedByName = () => null,
  getTimerStartedAt = () => null,
  startGroupTimer,
  resetGroupTimer,
  assetListProps,
}) {
  const renderDesktopGroupContent = (groupName, items, groupTitle) => {
    const timerRemaining = isExitEmergency ? getTimerRemainingMs(groupName) : null;
    const timerStatus = isExitEmergency ? getTimerStatus(groupName) : 'idle';

    return (
      <>
        {isExitEmergency && (
          <SessionDesktopGroupTimer
            status={timerStatus}
            remaining={timerRemaining}
            startedByName={getTimerStartedByName(groupName)}
            startedAt={getTimerStartedAt(groupName)}
            onStart={() => startGroupTimer(groupName)}
            onRequestReset={() => setTimerResetGroupName(groupName)}
          />
        )}
        <SessionAssetList
          items={items}
          groupName={groupName}
          groupLabel={groupTitle}
          sessionAssetType={sessionAssetType}
          isExitEmergency={isExitEmergency}
          {...assetListProps}
        />
      </>
    );
  };

  return (
    <>
      {/* Desktop: selectable two-pane workspace */}
      <div className="session-workspace session-workspace-desktop" ref={workspaceRef}>
        <div
          className="session-workspace-left"
          style={{ width: leftPaneWidthPx }}
        >
          <ul className="session-workspace-left-list">
            {groupedAssets.map(({ groupName, items }) => {
              const label = getGroupDisplayLabel(groupName, items, sessionAssetType);
              return (
                <li key={groupName}>
                  <button
                    type="button"
                    className={`session-workspace-left-item ${selectedGroupKey === groupName ? 'active' : ''}`}
                    onClick={() => setSelectedGroupKey(groupName)}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div
          className="session-workspace-resizer"
          role="separator"
          aria-label="Resize panes"
          onMouseDown={() => setIsResizing(true)}
          onTouchStart={() => setIsResizing(true)}
          style={{ userSelect: isResizing ? 'none' : undefined }}
        />
        <div className="session-workspace-right">
          {selectedGroupKey ? (() => {
            const group = groupedAssets.find((g) => g.groupName === selectedGroupKey);
            if (!group) return <div className="session-workspace-empty">Select a group</div>;
            const { groupName, items } = group;
            const groupTitle = getGroupDisplayLabel(groupName, items, sessionAssetType);
            return (
              <div className="session-workspace-right-inner">
                <h3 className="card-title session-workspace-right-title">{groupTitle}</h3>
                {renderDesktopGroupContent(groupName, items, groupTitle)}
              </div>
            );
          })() : (
            <div className="session-workspace-empty">Select a group from the list</div>
          )}
        </div>
      </div>

      {/* Mobile: collapsible groups — tap header to expand/collapse assets inline */}
      <div className="session-workspace-mobile">
        <div className="session-mobile-collapsible-groups">
          {groupedAssets.map(({ groupName, items }, index) => {
            const label = getGroupDisplayLabel(groupName, items, sessionAssetType);
            const expanded = expandedGroupKeys.includes(groupName);
            const contentId = `session-group-content-${index}`;
            const fields = deriveMobileGroupCardFields(
              groupName,
              items,
              sessionAssetType,
              assetListProps.results
            );
            const toggleGroup = () => {
              setExpandedGroupKeys((prev) =>
                prev.includes(groupName) ? prev.filter((k) => k !== groupName) : [...prev, groupName]
              );
            };
            return (
              <div key={groupName} className="session-mobile-group-block">
                {isExitEmergency ? (
                  <MobileAssetGroupEmerg
                    groupCode={fields.groupCode}
                    unitCount={fields.totalCount}
                    testedCount={fields.testedCount}
                    totalCount={fields.totalCount}
                    status={getTimerStatus(groupName)}
                    remainingMs={getTimerRemainingMs(groupName)}
                    expanded={expanded}
                    onToggle={toggleGroup}
                    onStart={() => startGroupTimer(groupName)}
                    onRequestReset={() => setTimerResetGroupName(groupName)}
                    aria-controls={contentId}
                  />
                ) : (
                  <MobileAssetGroupDefault
                    groupCode={fields.groupCode}
                    assetType={fields.assetType}
                    location={fields.location}
                    testedCount={fields.testedCount}
                    totalCount={fields.totalCount}
                    expanded={expanded}
                    onClick={toggleGroup}
                    aria-controls={contentId}
                  />
                )}
                {expanded && (
                  <div id={contentId} className="session-mobile-group-content" role="region" aria-label={`Group ${label}`}>
                    <SessionAssetList
                      items={items}
                      groupName={groupName}
                      groupLabel={label}
                      sessionAssetType={sessionAssetType}
                      isExitEmergency={isExitEmergency}
                      {...assetListProps}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {timerResetGroupName != null && (() => {
          const group = groupedAssets.find((g) => g.groupName === timerResetGroupName);
          const resetLabel = group ? getGroupDisplayLabel(group.groupName, group.items, sessionAssetType) : timerResetGroupName;
          return (
            <SessionTimerResetDialog
              label={resetLabel}
              onConfirm={() => {
                resetGroupTimer(timerResetGroupName);
                setTimerResetGroupName(null);
              }}
              onCancel={() => setTimerResetGroupName(null)}
            />
          );
        })()}
      </div>
    </>
  );
}
